import { FC, useEffect, useMemo, useState } from 'react';
import { Button, Card, Divider, RadixTable } from '@features/ui';
import {
  periodOptions,
  groupByOptions,
} from '@features/admin/reports/reportData';
import {
  ReportPeriod,
  ReportGroupBy,
  TReportPaymentByUserGroupData,
  TReportPaymentByUserGroupParams,
} from '@src/types/reports';
import { TOption } from '@src/types';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend,
} from 'recharts';
import { useReportFilters } from '@hooks/useReportFilters';
import { useReportTableColumns } from '@hooks/useReportTableColumns';
import { useReportChartData, getChartColor } from '@hooks/useReportChartData';
import {
  exportPaymentsByUserGroups,
  getPaymentsByUserGroupsGroupOptions,
  getPaymentsByUserGroups,
} from '@src/services/reports';
import dayjs from 'dayjs';
import { toast } from 'react-toastify';
import { downloadFile } from '@src/lib/utils';
import { canAccess } from '@src/utils/auth';
import { UserRolePermission } from '@src/types/settings';
import { useAuthStore } from '@src/stores/authStore';

import { ReportFilteringSettings } from '../ReportFilteringSettings';

interface TooltipProps {
  active?: boolean;
  payload?: Array<{
    value: number;
    dataKey: string;
    color: string;
    payload: any;
    name: string;
  }>;
}

const CustomTooltip: FC<TooltipProps> = ({ active, payload }) => {
  if (active && payload && payload.length) {
    return (
      <div
        className="bg-card-stat p-2.5 rounded-lg text-xs border border-[#3D3D3D] flex flex-col gap-y-1 min-w-[200px]"
        style={{ boxShadow: '0px 7px 13px 0px #B6D72C14' }}
      >
        <p className="text-[#999999] text-[10px] text-center mb-1">
          {payload[0]?.payload?.date}
        </p>
        {payload.map((entry, index) => (
          <div key={index} className="flex justify-between items-center gap-4">
            <span className="text-gray-300 text-xs">{entry.name}:</span>
            <span
              className="text-white font-semibold"
              style={{ color: entry.color }}
            >
              ${entry.value.toLocaleString()}
            </span>
          </div>
        ))}
      </div>
    );
  }

  return null;
};

export const PaymentsByUserGroup: FC = () => {
  const { auth } = useAuthStore();
  const [data, setData] = useState<TReportPaymentByUserGroupData[]>([]);
  const [userGroupFilterOptions, setUserGroupFilterOptions] = useState<
    TOption[]
  >([]);

  const filters = useReportFilters({
    defaultPeriod: ReportPeriod.LAST_7_DAYS,
    defaultGroupBy: ReportGroupBy.DAY,
    enableComparisons: true,
    enableUserGroups: true,
  });

  const selectedItemsForDisplay = useMemo(() => {
    if (filters.selectedUserGroups.length === 0) {
      return [];
    }

    return userGroupFilterOptions
      .filter((option) => filters.selectedUserGroups.includes(option.value))
      .map((option) => option.label);
  }, [filters.selectedUserGroups, userGroupFilterOptions]);

  const { columns, tableData, displayItems } = useReportTableColumns({
    data,
    selectedItems: selectedItemsForDisplay,
    itemsField: 'user_groups',
    compareToPreviousPeriod: filters.compareToPreviousPeriod,
    compareToSamePeriodPreviousYear: filters.compareToSamePeriodPreviousYear,
    getAccessField: (item) => item.date,
    getMainDataField: (item, key) => item.user_groups[key] || 0,
    getPreviousPeriodField: (item, key) =>
      item.user_groups_previous?.[key] || undefined,
    getPreviousYearField: (item, key) =>
      item.user_groups_previous_year?.[key] || undefined,
    columnTitleFormatter: (key) => key.toUpperCase(),
    valueFormatter: (_, value) => `$${value.toLocaleString()}`,
    includeTotalsRow: true,
  });

  const chartData = useReportChartData({
    data,
    displayItems,
    compareToPreviousPeriod: filters.compareToPreviousPeriod,
    compareToSamePeriodPreviousYear: filters.compareToSamePeriodPreviousYear,
    getAccessField: (item) => item.date,
    getMainDataField: (item, key) => item.user_groups[key] || 0,
    getPreviousPeriodField: (item, key) =>
      item.user_groups_previous?.[key] || undefined,
    getPreviousYearField: (item, key) =>
      item.user_groups_previous_year?.[key] || undefined,
  });

  const fetchUserGroupOptions = async () => {
    try {
      const options = await getPaymentsByUserGroupsGroupOptions();

      setUserGroupFilterOptions(
        options.map((option) => ({
          label: option.user_group_name,
          value: String(option.user_group_id),
        }))
      );
    } catch (error) {
      console.error('Error fetching user group options:', error);
      toast.error('Failed to load user group options');
    }
  };

  const buildPayload = (): TReportPaymentByUserGroupParams => {
    return {
      start_date: filters.startDate
        ? dayjs(filters.startDate).format('YYYY-MM-DD')
        : null,
      end_date: filters.endDate
        ? dayjs(filters.endDate).format('YYYY-MM-DD')
        : null,
      grouping: filters.selectedGroupBy as ReportGroupBy,
      user_groups:
        filters.selectedUserGroups.length > 0
          ? filters.selectedUserGroups
              .map((value) => Number(value))
              .filter((value) => Number.isFinite(value))
          : undefined,
      compare_to_previous_period: filters.compareToPreviousPeriod,
      compare_to_same_period_previous_year:
        filters.compareToSamePeriodPreviousYear,
    };
  };

  const handleRunReport = async () => {
    filters.setIsLoading(true);
    try {
      const response = await getPaymentsByUserGroups(buildPayload());

      setData(response.data);
      toast.success('Report generated successfully!');
    } catch (error) {
      console.error('Error fetching report:', error);
      toast.error('Error fetching report. Please try again.');
    } finally {
      filters.setIsLoading(false);
    }
  };

  const handleExportReport = async () => {
    try {
      const response = await exportPaymentsByUserGroups(buildPayload());

      downloadFile(response.data, 'payments-by-user-group-report');
      toast.success('Report exported successfully!');
    } catch (error) {
      console.error('Error exporting report:', error);
      toast.error('Error exporting report. Please try again.');
    }
  };

  useEffect(() => {
    fetchUserGroupOptions();
  }, []);

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-semibold text-white">
        Payment By User Group
      </h1>

      <Card className="p-6">
        <ReportFilteringSettings
          compareToPreviousPeriod={filters.compareToPreviousPeriod}
          compareToSamePeriodPreviousYear={
            filters.compareToSamePeriodPreviousYear
          }
          dateRangeValue={filters.valueStart}
          groupByOptions={groupByOptions}
          isLoading={filters.isLoading}
          periodOptions={periodOptions}
          periodRange={filters.periodRange}
          selectedGroupBy={filters.selectedGroupBy}
          selectedPeriod={filters.selectedPeriod}
          selectedUserGroups={filters.selectedUserGroups}
          showCompareToPreviousPeriod={filters.showPreviousPeriodCheckbox}
          showCompareToSamePeriodPreviousYear={filters.showPreviousYearCheckbox}
          showUserGroupsFilter={true}
          userGroupOptions={userGroupFilterOptions}
          onCompareToPreviousPeriodChange={filters.setCompareToPreviousPeriod}
          onCompareToSamePeriodPreviousYearChange={
            filters.setCompareToSamePeriodPreviousYear
          }
          onDateRangeChange={filters.setValueStart}
          onGroupByChange={filters.setSelectedGroupBy}
          onPeriodChange={filters.setSelectedPeriod}
          onRunReport={handleRunReport}
          onUserGroupsChange={filters.setSelectedUserGroups}
        />
      </Card>

      <Card className="p-6">
        <div className="space-y-4">
          <ResponsiveContainer height={400} width="100%">
            <LineChart data={chartData}>
              <CartesianGrid stroke="#333" strokeDasharray="3 3" />
              <XAxis
                dataKey="date"
                stroke="#999"
                style={{ fontSize: '12px' }}
              />
              <YAxis
                stroke="#999"
                style={{ fontSize: '12px' }}
                tickFormatter={(value) => `$${value.toLocaleString()}`}
              />
              <Tooltip content={<CustomTooltip />} />
              <Legend
                iconType="line"
                wrapperStyle={{ fontSize: '12px', paddingTop: '20px' }}
              />

              {/* Main data lines */}
              {displayItems.map((item, index) => (
                <Line
                  key={item}
                  activeDot={{ r: 6 }}
                  dataKey={item}
                  dot={{ fill: getChartColor(index), r: 4 }}
                  name={item}
                  stroke={getChartColor(index)}
                  strokeWidth={2}
                  type="monotone"
                />
              ))}

              {/* Previous period lines */}
              {filters.compareToPreviousPeriod &&
                displayItems.map((item, index) => (
                  <Line
                    key={`${item}_previous_period`}
                    activeDot={{ r: 4 }}
                    dataKey={`${item}_previous_period`}
                    dot={{
                      fill: getChartColor(index),
                      r: 3,
                    }}
                    name={`${item} (Previous Period)`}
                    stroke={getChartColor(index)}
                    strokeDasharray="5 5"
                    strokeWidth={1.5}
                    type="monotone"
                  />
                ))}

              {/* Previous year lines */}
              {filters.compareToSamePeriodPreviousYear &&
                displayItems.map((item, index) => (
                  <Line
                    key={`${item}_previous_year`}
                    activeDot={{ r: 4 }}
                    dataKey={`${item}_previous_year`}
                    dot={{
                      fill: getChartColor(index),
                      r: 3,
                    }}
                    name={`${item} (Previous Year)`}
                    stroke={getChartColor(index)}
                    strokeDasharray="2 2"
                    strokeWidth={1.5}
                    type="monotone"
                  />
                ))}
            </LineChart>
          </ResponsiveContainer>
        </div>
      </Card>

      <Card className="p-6">
        <div className="space-y-4">
          <h2 className="text-lg font-semibold text-white">Detailed Data</h2>
          <RadixTable columns={columns} data={tableData} />
          <Divider className="my-4" />
          {canAccess(
            UserRolePermission.EXPORT_REPORTS,
            auth?.user?.permissions
          ) && (
            <div className="flex justify-end">
              <Button
                className="text-[#B6D72C] border-[#B6D72C] hover:bg-[#B6D72C] hover:text-black"
                variant="outline"
                onClick={handleExportReport}
              >
                Download Excel
              </Button>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};
